import { Box } from '@sequencehq/core-components'
import { Global } from '@emotion/react'
import { FC, ReactNode } from 'react'

/**
 * The Sequence Labs activation beam: a colourful conic beam that travels once
 * around the wrapped card's border (stroke + inner glow + blurred bloom) with a
 * slow hue-shift, then fades out. Remount it (via a changing `key`) to replay.
 *
 * Trimmed from the "md" size / "colorful" variant of jakubantalik/border-beam
 * (MIT) down to the single light-theme configuration this card uses — the tuning
 * lives in the module constants below.
 */

// Colourful palette for the 9 border "blobs" (border-beam `colorPalettes.colorful`).
const COLORFUL_BORDER: { color: string; pos: string; size: string }[] = [
  { color: 'rgb(255, 50, 100)', pos: '33% -7.4%', size: '70px 40px' },
  { color: 'rgb(40, 140, 255)', pos: '12% -5%', size: '60px 35px' },
  { color: 'rgb(255, 120, 40)', pos: '2.1% 68.3%', size: '40px 70px' },
  { color: 'rgb(30, 185, 170)', pos: '2.1% 68.3%', size: '20px 35px' },
  { color: 'rgb(100, 70, 255)', pos: '74.4% 100%', size: '180px 32px' },
  { color: 'rgb(40, 140, 255)', pos: '55% 100%', size: '85px 26px' },
  { color: 'rgb(255, 120, 40)', pos: '93.9% 0%', size: '74px 32px' },
  { color: 'rgb(240, 50, 180)', pos: '100% 27.1%', size: '26px 42px' },
  { color: 'rgb(180, 40, 240)', pos: '100% 27.1%', size: '52px 48px' }
]

// Beam tuning (border-beam "md" size, light theme).
const BORDER_RADIUS = 8 // matches the SettingsGroup card
const BORDER_WIDTH = 1
const INNER_RADIUS = BORDER_RADIUS - BORDER_WIDTH
const DURATION = 1.8 // seconds for one lap
const START_ANGLE = 125 // where the beam sits/starts on the border, in degrees
const SWEEP = 440 // degrees travelled over DURATION
const FADE_IN = 0.25
const FADE_OUT = 0.75
const HUE_RANGE = 30 // ± degrees of hue-shift
const STRENGTH = 0.6 // overall strength of the beam/glow/bloom layers (0–1)
const STROKE_OPACITY = 0.82
const INNER_OPACITY = 0.26
const BLOOM_OPACITY = 0.34
const INNER_SHADOW = 'rgba(0, 0, 0, 0.14)'
const B = '1.35' // brightness filter
const S = '1.80' // saturation filter

// The 9-blob colourful mesh as a layered radial-gradient.
const COLORFUL_MESH = COLORFUL_BORDER.map(
  blob =>
    `radial-gradient(ellipse ${blob.size} at ${blob.pos}, ${blob.color}, transparent)`
).join(',\n    ')

// Inner glow: same blobs at 90% size and 0.45 alpha.
const INNER_GRADIENTS = COLORFUL_BORDER.map(blob => {
  const smaller = blob.size
    .split(' ')
    .map(part => `${Math.round(parseInt(part, 10) * 0.9)}px`)
    .join(' ')
  const faded = `color-mix(in srgb, ${blob.color} 45%, transparent)`
  return `radial-gradient(ellipse ${smaller} at ${blob.pos}, ${faded}, transparent)`
}).join(',\n    ')

// A single bright "comet" sweep along the border: a conic gradient that ramps up
// and back down through `stops` ([position%, alpha]), bookended by transparent.
const conicArc = (
  angleFrom: string,
  lead: number,
  trail: number,
  trailEnd: number | null,
  stops: [number, number][]
) =>
  `conic-gradient(
        from ${angleFrom},
        transparent 0%, transparent ${lead}%,
        ${stops.map(([pos, alpha]) => `rgba(0, 0, 0, ${alpha}) ${pos}%`).join(',\n        ')},
        transparent ${trail}%${trailEnd === null ? '' : `, transparent ${trailEnd}%`}
      )`

function generateBeamCSS(id: string): string {
  // Every conic reads the spinning angle plus the start offset, so START_ANGLE
  // rotates where the beam begins/sits on the border.
  const angleFrom = `calc(var(--beam-angle-${id}) + ${START_ANGLE}deg)`

  // A single full lap that stays at full opacity the whole way round, then fades
  // out only after the lap completes (delayed by `DURATION`) so the beam travels
  // the entire border before disappearing (remount via `key` to replay). Keep the
  // fade-in short so the beam is lit for most of the lap (a slow fade-in eats the
  // first part of the lap and makes it read as a partial lap).
  const wrapperAnimation = `beam-spin-${id} ${DURATION}s linear 1 forwards,
    beam-fade-in-${id} ${FADE_IN}s ease forwards,
    beam-fade-out-${id} ${FADE_OUT}s ease ${DURATION}s forwards`

  const strokeGradient = conicArc(angleFrom, 54, 78, 100, [
    [57, 0.08],
    [60, 0.2],
    [63, 0.4],
    [66, 0.55],
    [69, 0.4],
    [72, 0.2],
    [75, 0.08]
  ])

  const bloomGradient = conicArc(angleFrom, 58, 82, null, [
    [62, 0.02],
    [65, 0.08],
    [67, 0.2],
    [69, 0.4],
    [70, 0.6],
    [70.5, 0.6],
    [71.5, 0.4],
    [73, 0.2],
    [75, 0.08],
    [78, 0.02]
  ])

  // Conic mask that gates each layer to the travelling arc.
  const beamMask = `conic-gradient(
      from ${angleFrom},
      transparent 0%, transparent 30%,
      rgba(255, 255, 255, 0.1) 36%, rgba(255, 255, 255, 0.35) 44%,
      white 52%, white 80%,
      rgba(255, 255, 255, 0.35) 86%, rgba(255, 255, 255, 0.1) 92%,
      transparent 95%, transparent 100%
    )`

  return `
@property --beam-angle-${id} {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: true;
}

@property --beam-opacity-${id} {
  syntax: "<number>";
  initial-value: 0;
  inherits: true;
}

[data-beam="${id}"] {
  position: relative;
  border-radius: ${BORDER_RADIUS}px;
  overflow: hidden;
}

[data-beam="${id}"][data-active] {
  animation:
    ${wrapperAnimation};
}

[data-beam="${id}"][data-active]::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: ${INNER_RADIUS}px;
  padding: ${BORDER_WIDTH}px;
  clip-path: inset(0 round ${BORDER_RADIUS}px);
  background: ${strokeGradient},
    ${COLORFUL_MESH};
  -webkit-mask:
    ${beamMask},
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: source-in, xor;
  mask:
    ${beamMask},
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: intersect, exclude;
  pointer-events: none;
  z-index: 2;
  opacity: calc(var(--beam-opacity-${id}) * ${STROKE_OPACITY} * ${STRENGTH});
  animation: beam-hue-shift-${id} 12s ease-in-out infinite;
}

[data-beam="${id}"][data-active]::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: ${BORDER_RADIUS}px;
  background: ${INNER_GRADIENTS};
  box-shadow: inset 0 0 9px 1px ${INNER_SHADOW};
  -webkit-mask-image:
    ${beamMask},
    linear-gradient(white, transparent 28px, transparent calc(100% - 28px), white),
    linear-gradient(to right, white, transparent 28px, transparent calc(100% - 28px), white);
  -webkit-mask-composite: source-in, source-over;
  mask-image:
    ${beamMask},
    linear-gradient(white, transparent 28px, transparent calc(100% - 28px), white),
    linear-gradient(to right, white, transparent 28px, transparent calc(100% - 28px), white);
  mask-composite: intersect, add;
  pointer-events: none;
  z-index: 1;
  opacity: calc(var(--beam-opacity-${id}) * ${INNER_OPACITY} * ${STRENGTH});
  clip-path: inset(0 round ${BORDER_RADIUS}px);
  animation: beam-hue-shift-${id} 12s ease-in-out infinite;
}

[data-beam="${id}"] [data-beam-bloom] {
  display: none;
  position: absolute;
  inset: 0;
  border-radius: ${INNER_RADIUS}px;
  clip-path: inset(0 round ${BORDER_RADIUS}px);
  background: ${bloomGradient};
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  padding: ${BORDER_WIDTH}px;
  filter: blur(8px) brightness(${B}) saturate(${S});
  pointer-events: none;
  z-index: 3;
  opacity: 0;
}

[data-beam="${id}"][data-active] [data-beam-bloom] {
  display: block;
  opacity: calc(var(--beam-opacity-${id}) * ${BLOOM_OPACITY} * ${STRENGTH});
}

@keyframes beam-spin-${id} {
  to { --beam-angle-${id}: ${SWEEP}deg; }
}

@keyframes beam-fade-in-${id} {
  to { --beam-opacity-${id}: 1; }
}

@keyframes beam-fade-out-${id} {
  from { --beam-opacity-${id}: 1; }
  to { --beam-opacity-${id}: 0; }
}

@keyframes beam-hue-shift-${id} {
  0% { filter: hue-rotate(-${HUE_RANGE}deg) brightness(${B}) saturate(${S}); }
  50% { filter: hue-rotate(${HUE_RANGE}deg) brightness(${B}) saturate(${S}); }
  100% { filter: hue-rotate(-${HUE_RANGE}deg) brightness(${B}) saturate(${S}); }
}

@media (prefers-reduced-motion: reduce) {
  [data-beam="${id}"][data-active],
  [data-beam="${id}"][data-active]::after,
  [data-beam="${id}"][data-active]::before,
  [data-beam="${id}"][data-active] [data-beam-bloom] {
    animation: none !important;
  }
}
`
}

// A stable id (not useId) scopes the rules to `[data-beam]` without minting a
// fresh set of @property/keyframe registrations on every replay remount — those
// share global names and browsers don't reliably unregister them. Only one beam
// is ever on screen at a time, so a single shared id is safe; the CSS is
// therefore generated once at module scope and emotion dedupes the <Global>.
const BEAM_ID = 'labs-border-beam'
const BEAM_CSS = generateBeamCSS(BEAM_ID)

export const LabsBorderBeam: FC<{
  children: ReactNode
  active?: boolean
  'data-testid'?: string
}> = ({ children, active = true, 'data-testid': dataTestId }) => {
  return (
    <>
      {/* Generated stylesheet: scoped by the [data-beam] id, drives the beam via
          @property + pseudo-elements that inline styles can't express. */}
      <Global styles={BEAM_CSS} />
      <Box
        data-beam={BEAM_ID}
        data-active={active ? '' : undefined}
        data-testid={dataTestId}
        width="100%"
      >
        {children}
        <Box data-beam-bloom />
      </Box>
    </>
  )
}
